import { SsoAuthService } from 'src/app/member/service/sso-auth.service';
import { TranslateService } from '@ngx-translate/core';
import { Injectable, Inject } from '@angular/core';
import { MemberProxyService } from 'src/app/member/service/member-proxy.service';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { GlobalComponentService } from '@techmore/global';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from 'src/app/web-generator/generator-env-token';
import { catchError, map, tap } from 'rxjs/operators';
import { CommonHttpClientService } from 'src/app/share/service/common-http-client.service';
import { MemberCollection, MemberCollectionTag } from 'src/app/member-collections/member-collections/memberCollection';
import { MemberTheme } from 'src/app/member-collections/member-collections/memberTheme';
import { MemberPreference } from 'src/app/web-generator/template-generator/component/member-preference-modal/member-preference';
import { ServerConfigService } from './server-config.service';
import { LoggerService } from './logger.service';
import { TokenService } from './token.service';
import { IS_BROWSER_TOKEN } from '../tokens/is-browser.token';

@Injectable({
  providedIn: 'root'
})
export class UserService extends SsoAuthService {
  /** 收藏主題項目 */
  collectedTheme$ = new BehaviorSubject<MemberTheme[]>([]);
  get collectedTheme() { return this.collectedTheme$.value; }
  /** 收藏資料項目 */
  collectedData$ = new BehaviorSubject<MemberCollection[]>([]);
  get collectedData() { return this.collectedData$.value; }

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) protected generatorEnv: GeneratorEnv,
    protected commonHttp: CommonHttpClientService,
    protected globalComponentService: GlobalComponentService,
    protected router: Router,
    protected memberService: MemberProxyService,
    protected translateService: TranslateService,
    @Inject(IS_BROWSER_TOKEN) protected isBrowser: boolean,
    protected loggerService: LoggerService,
    protected tokenService: TokenService,
    protected serverConfigService: ServerConfigService,
  ) {
    super(generatorEnv, commonHttp, globalComponentService, router, memberService, translateService, isBrowser, loggerService, tokenService);
  }

  init(memberProxyService: MemberProxyService) {
    memberProxyService.getMemberInfo$().subscribe(memberInfo => {
      if (memberInfo) {
        this.getCollectedData().subscribe();
        this.getCollectedTheme().subscribe();
      } else {
        this.collectedTheme$.next(undefined);
        this.collectedData$.next(undefined);
      }
    });
  }

  leftMessage = (fetchIndexCode: string, materialId: string, name: string, comment: string): Observable<any[]> => {
    const obsFunc = () => {
      return this.commonHttp.post<any[]>(`${this.generatorEnv.apiHost}/api/collection/detail/message/${fetchIndexCode}`, {
        name,
        message: comment,
        // createDate: moment().format('YYYY-MM-DDTHH:mm:ss.sss')
      }, {
        params: {
          uid: materialId,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 取得收藏偏好 */
  getPreference = (): Observable<MemberPreference> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.get<MemberPreference>(`${this.generatorEnv.apiHost}/api/member/subject/preference`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 變更收藏偏好 */
  updatePreference = (v): Observable<void> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.put<void>(`${this.generatorEnv.apiHost}/api/member/subject/preference`, v, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 取得收藏主題 */
  getCollectedTheme = (): Observable<MemberTheme[]> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.get<MemberTheme[]>(`${this.generatorEnv.apiHost}/api/member/theme`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        tap(v => {
          if (v) {
            this.collectedTheme$.next(v);
          }
        }),
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 取得收藏資料 */
  getCollectedData = (): Observable<MemberCollection[]> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.get<MemberCollection[]>(`${this.generatorEnv.apiHost}/api/member/collection`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        tap(v => {
          if (v) {
            this.collectedData$.next(v);
          }
        }),
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 收藏主題 */
  insertCollectedTheme = (id): Observable<any> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.post<any>(`${this.generatorEnv.apiHost}/api/theme/${this.serverConfigService.config.lang}/${id}`, {}, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 收藏資料 */
  insertCollectedData = (sourceType: string, uid: string): Observable<any> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.post<any>(`${this.generatorEnv.apiHost}/api/collection/detail/collect/${sourceType}`, {}, {
        params: {
          uid,
        },
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 刪除收藏主題 */
  deleteMemberTheme = (id): Observable<void> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.delete<void>(`${this.generatorEnv.apiHost}/api/member/theme/${id}`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 刪除收藏資料 */
  deleteMemberData = (id): Observable<void> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.delete<void>(`${this.generatorEnv.apiHost}/api/member/collection/${id}`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 取得推薦給會員的素材資料 */
  getRecommandData = (): Observable<any[]> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      // return timer(500).pipe(map(t => {
      //   return [
      //     {
      //       description: "",
      //       fetchIndex: "MOCCOLLECTIONS",
      //       format: null,
      //       height: null,
      //       iframeUrl: null,
      // tslint:disable-next-line: max-line-length
      //       image: "https://collections.culture.tw/ShowGalImage.aspx?SYSUID=103&IMG=KHKBK2MCMFM5MEK25LMCMAMFMJ0BM6MFMWM4MI0VMUK25VM4MAMBMJ0LK8KJKLKPKQMSM20OKGKK5RM6K2KE5TMZK7M9K2KMK75OKM0O55KL5SK7M25OKB530BMYMF0WME0GM2M604M4MS0EMA&TEXT=K7KG575Y&FROM=5YKK57&WM=0x010000000de89bf65dbd599b205c8663099864cdfcddf521881ca63c85cec5c60eb560181f9162467c4a5b50&GDMID=0x010000004c746d9f913137a4457d228fa0223cb723eb309854435d80",
      //       imageUrl: null,
      //       materialId: "103000006447",
      //       materialName: "木雕達摩",
      //       rights: null,
      //       width: null,
      //     }
      //   ];
      // }));
      return this.http.get<{
        subject: string,
        title: string,
        description: string,
        sourceType: string,
        uid: string,
        iframeUrl: string
      }>(`${this.generatorEnv.apiHost}/api/member/collection/recommend`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 取得收藏標籤列表 */
  getCollectedTagList = (): Observable<MemberCollectionTag[]> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.get<{ id: string, tag: string }[]>(`${this.generatorEnv.apiHost}/api/member/tags`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 新增收藏標籤 */
  insertCollectedTag = (tag: MemberCollectionTag): Observable<void> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.post<{ tag: string }>(`${this.generatorEnv.apiHost}/api/member/tag`, tag, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 修改收藏標籤 */
  updateCollectedTag = (uuid, tag: MemberCollectionTag): Observable<void> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.put<void>(`${this.generatorEnv.apiHost}/api/member/tag/${uuid}`, tag, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 刪除收藏標籤 */
  deleteCollectedTag = (uuid): Observable<void> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.delete<void>(`${this.generatorEnv.apiHost}/api/member/tag/${uuid}`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 取得所標記的主題id列表 */
  getCollectedTagTheme = (uuid): Observable<number[]> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.get<void>(`${this.generatorEnv.apiHost}/api/member/tag/${uuid}/thematicIds`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 收藏標籤新增主題 */
  updateCollectedTagTheme = (uuid, thematicIds: number[]): Observable<void> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.put<void>(`${this.generatorEnv.apiHost}/api/member/tag/${uuid}/thematic/${thematicIds}`, {}, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 收藏標籤移除主題 */
  deleteCollectedTagTheme = (uuid, thematicIds: number[]): Observable<void> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.delete<void>(`${this.generatorEnv.apiHost}/api/member/tag/${uuid}/thematic/${thematicIds}`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 取得所標記的資料id列表 */
  getCollectedTagData = (uuid): Observable<number[]> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.get<void>(`${this.generatorEnv.apiHost}/api/member/tag/${uuid}/collectionIds`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 收藏標籤新增資料 */
  updateCollectedTagData = (uuid, collectionIds: number[]): Observable<void> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.put<void>(`${this.generatorEnv.apiHost}/api/member/tag/${uuid}/collection/${collectionIds}`, {}, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }

  /** 收藏標籤移除資料 */
  deleteCollectedTagData = (uuid, collectionIds: number[]): Observable<void> => {
    const obsFunc = () => {
      const authToken = localStorage.getItem('auth_token');
      const accessToken = this.generatorEnv.commonToken;
      return this.http.delete<void>(`${this.generatorEnv.apiHost}/api/member/tag/${uuid}/collection/${collectionIds}`, {
        headers: {
          access_token: accessToken,
          Authorization: `Bearer ${authToken}`
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable, obsFunc)),
      );
    };
    return obsFunc();
  }
}
